import React, { useCallback, useEffect, useRef, useState } from "react";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  TextField,
} from "@mui/material";
import {
  CloudUpload,
  Close,
  Edit,
  Delete,
  Download,
} from "@mui/icons-material";
import * as XLSX from "xlsx";
import toast from "react-hot-toast";
import { useDispatch } from "react-redux";
import {
  uploadUnlistedReconciliation,
  getUnlistedReconciliationHistory,
  updateUnlistedReconciliation,
  deleteUnlistedReconciliation,
} from "../../redux/admin/adminSlice";

interface ReconRow {
  id: number;
  statementDate: string;
  reportGenerationDate: string;
  stockName: string;
  isin: string;
  qtyInSoftware: number;
  qtyInDp: number;
  mismatch: number;
  fileName: string;
  dpAccountName: string | null;
  createdAt: string;
}

const getDpType = (fileName: string): "CDSL" | "NSDL" | null => {
  const match = fileName.match(/DPHOLD_([\d]+)/i);
  if (!match) return null;
  const id = match[1];
  if (id.startsWith("00")) return "CDSL";
  if (id.startsWith("10")) return "NSDL";
  return null;
};

const InventoryReconciliation: React.FC = () => {
  const dispatch = useDispatch<any>();
  const [isDragging, setIsDragging] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [history, setHistory] = useState<ReconRow[]>([]);
  const [historyLoading, setHistoryLoading] = useState(true);
  const [lastUploadedFile, setLastUploadedFile] = useState<string | null>(null);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [editModalOpen, setEditModalOpen] = useState(false);
  const [editForm, setEditForm] = useState<Partial<ReconRow>>({});
  const [updatingRow, setUpdatingRow] = useState(false);
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{ fileName: string; reportGenerationDate: string } | null>(null);
  const [deleting, setDeleting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const fetchHistory = useCallback(async () => {
    setHistoryLoading(true);
    try {
      const res = await dispatch(getUnlistedReconciliationHistory()).unwrap();
      if (res.statusCode === 200) setHistory(res.data || []);
    } catch {
      toast.error("Failed to fetch reconciliation history");
    } finally {
      setHistoryLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const handleUpload = useCallback(async (file: File) => {
    if (
      !file.name.toLowerCase().endsWith(".htm") &&
      !file.name.toLowerCase().endsWith(".html")
    ) {
      toast.error("Please upload a valid .htm / .html DP statement file");
      return;
    }
    setUploading(true);
    setLastUploadedFile(file.name);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const res = await dispatch(uploadUnlistedReconciliation(formData)).unwrap();
      if (res.statusCode === 200) {
        toast.success("Reconciliation complete! Results saved.");
        setUploadModalOpen(false);
        await fetchHistory();
      } else {
        toast.error(res.message || "Upload failed");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Upload failed");
    } finally {
      setUploading(false);
    }
  }, [dispatch, fetchHistory]);

  const onDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) handleUpload(file);
  }, [handleUpload]);

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) handleUpload(file);
    e.target.value = "";
  };

  const handleEditClick = (row: ReconRow) => {
    setEditForm({ ...row });
    setEditModalOpen(true);
  };

  const handleUpdate = async () => {
    if (!editForm.id) return;
    setUpdatingRow(true);
    try {
      const res = await dispatch(updateUnlistedReconciliation({
        id: editForm.id,
        qtyInSoftware: Number(editForm.qtyInSoftware),
        qtyInDp: Number(editForm.qtyInDp),
        mismatch: Number(editForm.qtyInSoftware || 0) - Number(editForm.qtyInDp || 0),
      })).unwrap();
      if (res.statusCode === 200) {
        toast.success("Record updated successfully");
        setEditModalOpen(false);
        fetchHistory();
      } else {
        toast.error(res.message || "Update failed");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Update failed");
    } finally {
      setUpdatingRow(false);
    }
  };

  const handleDeleteClick = (group: ReconRow[]) => {
    setDeleteTarget({
      fileName: group[0].fileName,
      reportGenerationDate: group[0].reportGenerationDate,
    });
    setDeleteConfirmOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    setDeleting(true);
    try {
      const res = await dispatch(deleteUnlistedReconciliation(deleteTarget)).unwrap();
      if (res.statusCode === 200) {
        toast.success("Report deleted successfully");
        setDeleteConfirmOpen(false);
        setDeleteTarget(null);
        fetchHistory();
      } else {
        toast.error(res.message || "Delete failed");
      }
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Delete failed");
    } finally {
      setDeleting(false);
    }
  };

  const handleDownload = (group: ReconRow[]) => {
    const dpType = getDpType(group[0].fileName);
    const sheetData = group.map((r) => ({
      "Script Name": r.stockName,
      "ISIN": r.isin,
      "Qty in Software": r.qtyInSoftware,
      "Qty in DP": r.qtyInDp,
      "Mismatch": r.mismatch,
      "Statement Date": r.statementDate ? new Date(r.statementDate.replace("Z", "")).toLocaleString("en-IN") : "",
      "Report Generated On": r.reportGenerationDate ? new Date(r.reportGenerationDate.replace("Z", "")).toLocaleString("en-IN") : "",
      "DP Account Name": r.dpAccountName || "",
      "Depository": dpType || "",
    }));
    const worksheet = XLSX.utils.json_to_sheet(sheetData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Reconciliation");
    const baseName = group[0].fileName.replace(/\.htm[l]?$/i, "");
    XLSX.writeFile(workbook, `${baseName}_reconciliation.xlsx`);
  };

  const groupedHistory = React.useMemo(() => {
    const groups: { [key: string]: ReconRow[] } = {};
    const orderedKeys: string[] = [];
    history.forEach((r) => {
      // Group by file name + report date to handle multiple identical file uploads
      const groupKey = `${r.fileName}_${r.reportGenerationDate}`;
      if (!groups[groupKey]) {
        groups[groupKey] = [];
        orderedKeys.push(groupKey);
      }
      groups[groupKey].push(r);
    });
    return orderedKeys.map((key) => {
      return [...groups[key]].sort((a, b) => a.stockName.localeCompare(b.stockName));
    });
  }, [history]);

  // const mismatchCount = history.filter((r) => r.mismatch !== 0).length;

  return (
    <div
      className="min-h-screen bg-lightBg py-8 px-4 sm:px-6 lg:px-8"
      style={{ display: "flex", flexDirection: "column", alignItems: "center", marginBottom: "20px" }}
    >
    <Box sx={{ width: "98%" }}>
      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
        mb={3}
      >
        <Box>
          <Typography variant="h5" fontWeight={700}>
            DP Reconciliation
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={0.5}>
            Upload an NSDL / CDSL holding statement (.htm) to reconcile against
            software inventory
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<CloudUpload />}
          onClick={() => setUploadModalOpen(true)}
          sx={{ textTransform: "none", px: 3, py: 1.2, fontSize: 15 }}
        >
          Upload Statement
        </Button>
      </Box>

      <Dialog
        open={uploadModalOpen}
        onClose={() => !uploading && setUploadModalOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ m: 0, p: 2, fontWeight: 700 }}>
          Upload Holding Statement
        </DialogTitle>
        <IconButton
          aria-label="close"
          onClick={() => setUploadModalOpen(false)}
          disabled={uploading}
          sx={{ position: "absolute", right: 8, top: 8, color: "grey.500" }}
        >
          <Close />
        </IconButton>
        <DialogContent dividers sx={{ p: 4 }}>
          <Paper
            onDragOver={(e) => {
              e.preventDefault();
              setIsDragging(true);
            }}
            onDragLeave={() => setIsDragging(false)}
            onDrop={onDrop}
            onClick={() => !uploading && fileInputRef.current?.click()}
            elevation={0}
            sx={{
              border: "2px dashed",
              borderColor: isDragging ? "primary.main" : "grey.300",
              borderRadius: 3,
              p: 5,
              textAlign: "center",
              cursor: uploading ? "not-allowed" : "pointer",
              backgroundColor: isDragging ? "primary.50" : "grey.50",
              transition: "all 0.2s ease",
              "&:hover": {
                borderColor: "primary.main",
                backgroundColor: "primary.50",
              },
            }}
          >
            <input
              ref={fileInputRef}
              type="file"
              accept=".htm,.html"
              style={{ display: "none" }}
              onChange={onFileChange}
            />
            {uploading ? (
              <Box
                display="flex"
                flexDirection="column"
                alignItems="center"
                gap={1.5}
              >
                <CircularProgress size={40} />
                <Typography color="text.secondary" fontWeight={500}>
                  Processing <strong>{lastUploadedFile}</strong>…
                </Typography>
              </Box>
            ) : (
              <Box>
                <CloudUpload sx={{ fontSize: 48, color: "grey.400", mb: 1 }} />
                <Typography variant="h6" fontWeight={600} color="text.primary">
                  Drag & drop your DP statement here
                </Typography>
                <Typography variant="body2" color="text.secondary" mt={0.5}>
                  Supports NSDL and CDSL <strong>.htm</strong> holding statement
                  files
                </Typography>
                <Button
                  variant="contained"
                  sx={{ mt: 2, textTransform: "none", borderRadius: 2 }}
                  onClick={(e) => {
                    e.stopPropagation();
                    fileInputRef.current?.click();
                  }}
                >
                  Browse File
                </Button>
              </Box>
            )}
          </Paper>
        </DialogContent>
      </Dialog>

      {historyLoading ? (
        <Box display="flex" justifyContent="center" py={4}>
          <CircularProgress size={30} />
        </Box>
      ) : groupedHistory.length > 0 ? (
        groupedHistory.map((group, groupIdx) => (
          <Paper key={groupIdx} elevation={1} sx={{ borderRadius: 3, mb: 4, overflow: "hidden" }}>
            {/* Result header */}
            <Box
              display="flex"
              justifyContent="space-between"
              alignItems="center"
              px={3}
              py={2.5}
              sx={{ borderBottom: "2px solid", borderColor: "primary.main", bgcolor: "#e8f0fe" }}
            >
              <Box display="flex" alignItems="center" gap={1.5}>
                <Box>
                  <Box display="flex" alignItems="center" gap={1}>
                    <Typography fontWeight={700} fontSize={18} color="text.primary">
                      {group[0].fileName}
                    </Typography>
                    {(() => {
                      const dpType = getDpType(group[0].fileName);
                      if (!dpType) return null;
                      return (
                        <Chip
                          label={dpType}
                          size="small"
                          sx={{
                            fontWeight: 700,
                            fontSize: 11,
                            height: 20,
                            bgcolor: dpType === "CDSL" ? "#e3f2fd" : "#e8f5e9",
                            color: dpType === "CDSL" ? "#1565c0" : "#2e7d32",
                            border: "1px solid",
                            borderColor: dpType === "CDSL" ? "#90caf9" : "#a5d6a7",
                          }}
                        />
                      );
                    })()}
                  </Box>
                  {group[0].dpAccountName && (
                    <Typography variant="body1" color="text.primary" fontWeight={600} mt={0.5}>
                      DP Account: {group[0].dpAccountName}
                    </Typography>
                  )}
                  {(() => {
                    const stmtRaw = group[0].statementDate;
                    const repRaw = group[0].reportGenerationDate;
                    
                    // Strip trailing Z so browser parses as exact local time without shifting
                    const stmtDate = stmtRaw ? new Date(stmtRaw.replace('Z', '')) : null;
                    const repDate = repRaw ? new Date(repRaw.replace('Z', '')) : null;

                    const stmtStr = stmtDate ? stmtDate.toLocaleString("en-IN", {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', second: '2-digit',
                      hour12: false
                    }) : "—";

                    const repStr = repDate ? repDate.toLocaleString("en-IN", {
                      day: '2-digit', month: '2-digit', year: 'numeric',
                      hour: '2-digit', minute: '2-digit', second: '2-digit',
                      hour12: true
                    }) : "—";

                    return (
                      <Typography variant="body2" color="text.primary" display="block" mt={0.5} fontSize={13}>
                        <strong>Holding Statement Date:</strong> {stmtStr}
                        &nbsp;&nbsp;•&nbsp;&nbsp;
                        <strong>Report Generated On:</strong> {repStr.toUpperCase()}
                        &nbsp;&nbsp;•&nbsp;&nbsp;
                        <strong>{group.length} total ISINs</strong>
                      </Typography>
                    );
                  })()}
                </Box>
              </Box>
              <Box display="flex" alignItems="center" gap={1}>
                <Tooltip title="Download as Excel">
                  <IconButton
                    color="primary"
                    onClick={() => handleDownload(group)}
                    sx={{ bgcolor: "white", border: "1px solid", borderColor: "primary.main", "&:hover": { bgcolor: "primary.50" } }}
                  >
                    <Download />
                  </IconButton>
                </Tooltip>
                <Tooltip title="Delete this report">
                  <IconButton
                    color="error"
                    onClick={() => handleDeleteClick(group)}
                    sx={{ bgcolor: "white", border: "1px solid", borderColor: "error.main", "&:hover": { bgcolor: "#ffebee" } }}
                  >
                    <Delete />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            {/* Reconciliation Table */}
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ backgroundColor: "primary.main" }}>
                    <TableCell sx={{ fontWeight: 700, fontSize: 15, color: "white", py: 2 }}>Script Name</TableCell>
                    <TableCell sx={{ fontWeight: 700, fontSize: 15, color: "white", py: 2 }}>ISIN</TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: "white", py: 2 }}>
                      Qty in Software
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: "white", py: 2 }}>
                      Qty in DP
                    </TableCell>
                    <TableCell align="right" sx={{ fontWeight: 700, fontSize: 15, color: "white", py: 2 }}>
                      Mismatch
                    </TableCell>
                    <TableCell align="center" sx={{ fontWeight: 700, fontSize: 15, color: "white", py: 2 }}>
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {group.map((row) => {
                    const hasMismatch = row.mismatch !== 0;
                    return (
                      <TableRow
                        key={row.id}
                        sx={{
                          backgroundColor: hasMismatch
                            ? "rgba(211,47,47,0.06)"
                            : "inherit",
                          "&:hover": {
                            backgroundColor: hasMismatch
                              ? "rgba(211,47,47,0.12)"
                              : "#f0f4ff",
                          },
                        }}
                      >
                        <TableCell sx={{ py: 2 }}>
                          <Typography fontSize={15} fontWeight={hasMismatch ? 700 : 500} color="text.primary">
                            {row.stockName}
                          </Typography>
                        </TableCell>
                        <TableCell sx={{ py: 2 }}>
                          <Typography fontSize={15} fontWeight={500} color="text.primary">
                            {row.isin}
                          </Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography fontSize={15} color="text.primary">{row.qtyInSoftware.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography fontSize={15} color="text.primary">{row.qtyInDp.toLocaleString()}</Typography>
                        </TableCell>
                        <TableCell align="right" sx={{ py: 2 }}>
                          <Typography
                            fontSize={15}
                            fontWeight={700}
                            color={hasMismatch ? "error.main" : "success.main"}
                          >
                            {hasMismatch
                              ? row.mismatch > 0
                                ? `+${row.mismatch.toLocaleString()}`
                                : row.mismatch.toLocaleString()
                              : "0"}
                          </Typography>
                        </TableCell>
                        <TableCell align="center" sx={{ py: 2 }}>
                          <IconButton
                            color="primary"
                            onClick={() => handleEditClick(row)}
                          >
                            <Edit />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    );
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Paper>
        ))
      ) : (
        <Paper
          elevation={1}
          sx={{ borderRadius: 3, p: 6, textAlign: "center" }}
        >
          <Typography variant="h6" color="text.secondary" fontSize={18}>
            No data is available
          </Typography>
          <Typography variant="body1" color="text.secondary" mt={1}>
            Upload a DP holding statement to view reconciliation data.
          </Typography>
        </Paper>
      )}

      <Dialog
        open={editModalOpen}
        onClose={() => !updatingRow && setEditModalOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Edit Reconciliation</DialogTitle>
        <DialogContent dividers>
          <Box display="flex" flexDirection="column" gap={2.5} py={1}>
            <TextField
              label="Script Name"
              value={editForm.stockName || ""}
              disabled
              fullWidth
              variant="filled"
              size="small"
            />
            <TextField
              label="ISIN"
              value={editForm.isin || ""}
              disabled
              fullWidth
              variant="filled"
              size="small"
            />
            <TextField
              label="Qty in Software"
              type="number"
              value={editForm.qtyInSoftware || 0}
              onChange={(e) => {
                const val = Number(e.target.value);
                setEditForm({
                  ...editForm,
                  qtyInSoftware: val,
                  mismatch: val - (editForm.qtyInDp || 0),
                });
              }}
              fullWidth
              autoFocus
            />
            <TextField
              label="Qty in DP"
              type="number"
              value={editForm.qtyInDp || 0}
              onChange={(e) => {
                const val = Number(e.target.value);
                setEditForm({
                  ...editForm,
                  qtyInDp: val,
                  mismatch: (editForm.qtyInSoftware || 0) - val,
                });
              }}
              fullWidth
            />
            <Box
              sx={{
                p: 2,
                borderRadius: 2,
                bgcolor: (editForm.mismatch || 0) !== 0 ? "error.50" : "success.50",
                border: "1px solid",
                borderColor: (editForm.mismatch || 0) !== 0 ? "error.200" : "success.200",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Calculated Mismatch
              </Typography>
              <Typography
                variant="h6"
                fontWeight={700}
                color={(editForm.mismatch || 0) !== 0 ? "error.main" : "success.main"}
              >
                {editForm.mismatch && editForm.mismatch > 0 ? `+${editForm.mismatch}` : editForm.mismatch || 0}
              </Typography>
            </Box>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={() => setEditModalOpen(false)} disabled={updatingRow}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleUpdate}
            disabled={updatingRow}
            startIcon={updatingRow && <CircularProgress size={16} />}
          >
            {updatingRow ? "Saving..." : "Save Changes"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Delete Confirmation Dialog ───────────────────────────────── */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => !deleting && setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ fontWeight: 700 }}>Delete Report</DialogTitle>
        <DialogContent dividers>
          <Typography>
            Are you sure you want to delete all{" "}
            <strong>{deleteTarget?.fileName}</strong> records? This action cannot
            be undone.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button
            onClick={() => setDeleteConfirmOpen(false)}
            disabled={deleting}
          >
            Cancel
          </Button>
          <Button
            variant="contained"
            color="error"
            onClick={handleDeleteConfirm}
            disabled={deleting}
            startIcon={deleting && <CircularProgress size={16} />}
          >
            {deleting ? "Deleting..." : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </div>
  );
};

export default InventoryReconciliation;
